class TableTabs {
  private el: HTMLElement;
  private triggers: HTMLButtonElement[];
  private panels: HTMLElement[];
  private notePanels: HTMLElement[];
  private noteGroup: HTMLElement | null;
  private hasAnimatedNotes: boolean;
  private activeIndex = 0;
  private isAnimating = false;

  constructor(el: HTMLElement) {
    this.el = el;
    this.triggers = Array.from(this.el.querySelectorAll<HTMLButtonElement>('[data-table-tab-trigger]'));
    this.panels = Array.from(this.el.querySelectorAll<HTMLElement>('[data-table-tab-panel]'));
    this.notePanels = Array.from(this.el.querySelectorAll<HTMLElement>('[data-table-note-panel]'));
    this.noteGroup = this.el.querySelector<HTMLElement>('[data-table-note-group]');
    this.hasAnimatedNotes = this.notePanels.length > 0 && this.notePanels.length === this.triggers.length;

    if (!this.triggers.length || this.triggers.length !== this.panels.length) {
      return;
    }

    if (!this.hasAnimatedNotes) {
      this.notePanels = [];
      this.noteGroup = null;
    }

    this.init();
  }

  private init() {
    this.activeIndex = this.triggers.findIndex((trigger) => trigger.getAttribute('aria-selected') === 'true');
    if (this.activeIndex < 0) {
      this.activeIndex = 0;
    }

    this.updateNoteGroupVisibility(this.activeIndex);

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        this.activate(index);
      });
    });
  }

  private activate(activeIndex: number) {
    if (this.isAnimating || activeIndex === this.activeIndex) {
      return;
    }

    this.isAnimating = true;
    this.triggers.forEach((trigger, index) => {
      const isActive = index === activeIndex;

      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
      trigger.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    const panelGroups = [this.panels];
    if (this.hasAnimatedNotes) {
      panelGroups.push(this.notePanels);
    }
    let pendingGroups = panelGroups.length;

    panelGroups.forEach((group) => {
      this.swapPanelGroup(group, activeIndex, () => {
        pendingGroups -= 1;

        if (pendingGroups === 0) {
          this.activeIndex = activeIndex;
          this.isAnimating = false;
          this.el.dispatchEvent(new CustomEvent('table-tabs:changed', { bubbles: true }));
        }
      });
    });
  }

  private swapPanelGroup(panels: HTMLElement[], activeIndex: number, onComplete: () => void) {
    const currentPanel = panels[this.activeIndex];
    const nextPanel = panels[activeIndex];

    if (panels === this.notePanels) {
      this.swapNotePanels(currentPanel, nextPanel, activeIndex, onComplete);
      return;
    }

    this.fadeBetweenPanels(currentPanel, nextPanel, onComplete);
  }

  private swapNotePanels(
    currentPanel: HTMLElement,
    nextPanel: HTMLElement,
    activeIndex: number,
    onComplete: () => void,
  ) {
    if (!this.noteGroup) {
      this.fadeBetweenPanels(currentPanel, nextPanel, () => {
        this.updateNoteGroupVisibility(activeIndex);
        onComplete();
      });
      return;
    }

    const $noteGroup = $(this.noteGroup);

    $noteGroup.stop(true, true).fadeOut(200, () => {
      this.hidePanel(currentPanel);
      this.showPanel(nextPanel);
      this.updateNoteGroupVisibility(activeIndex);

      if (!this.hasVisibleNote(activeIndex)) {
        onComplete();
        return;
      }

      $noteGroup.hide().stop(true, true).fadeIn(200, onComplete);
    });
  }

  private fadeBetweenPanels(currentPanel: HTMLElement, nextPanel: HTMLElement, onComplete: () => void) {
    const $currentPanel = $(currentPanel);
    const $nextPanel = $(nextPanel);

    $currentPanel.stop(true, true).fadeOut(200, () => {
      this.hidePanel(currentPanel);
      this.showPanel(nextPanel);
      $nextPanel.hide().stop(true, true).fadeIn(200, onComplete);
    });
  }

  private hidePanel(panel: HTMLElement) {
    panel.setAttribute('hidden', '');
    panel.setAttribute('aria-hidden', 'true');
  }

  private showPanel(panel: HTMLElement) {
    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', 'false');
  }

  private updateNoteGroupVisibility(activeIndex: number) {
    if (!this.noteGroup) {
      return;
    }

    const shouldShow = this.hasVisibleNote(activeIndex);

    this.noteGroup.hidden = !shouldShow;
  }

  private hasVisibleNote(activeIndex: number): boolean {
    const activePanel = this.notePanels[activeIndex];

    if (!activePanel) {
      return false;
    }

    return activePanel.dataset.tableNoteHasContent === 'true';
  }
}

export default TableTabs;
